export default function AventuraDestinationsSection() {
  const stats = [
    {
      value: "65%",
      label: "of Aventura requests are Latin America or Caribbean travel with under 3 weeks notice",
    },
    {
      value: "2 airports",
      label: "within 30 minutes — MIA and FLL — giving Aventura unmatched departure flexibility",
    },
    {
      value: "Nov–Jan",
      label: "peak travel season — Art Basel, holiday family visits, and cruise departures converge",
    },
    {
      value: "24-hour",
      label: "shortest emergency window we've accommodated for an Aventura business traveler",
    },
  ];

  const destinations = [
    {
      icon: "ri-map-pin-2-line",
      name: "Colombia & Venezuela",
      context: "The most common family and business destinations for Aventura's large Colombian and Venezuelan communities. Bogota, Medellin, and Caracas are not vacation spots for many Aventura residents — they are home. Business executives also travel to Cali, Barranquilla, and Panama City for corporate meetings and deal closings. Direct flights from MIA and FLL make these trips routine, but passport validity requirements are strict.",
      urgency: "Family visit bookings often happen 2–4 weeks before travel — too short for standard processing if a passport has expired",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Brazil",
      context: "Sao Paulo and Rio de Janeiro draw Aventura's Brazilian residents for family visits, Carnival, and summer holidays (Brazil's summer is December–February, aligning perfectly with South Florida's snowbird season). Business travelers fly to Sao Paulo for finance, tech, and manufacturing meetings. Brazil requires six months of passport validity and has strict visa reciprocity rules for U.S. visitors.",
      urgency: "Carnival bookings spike in December — passport discoveries spike in January when travelers realize documents expired",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Argentina & Chile",
      context: "Buenos Aires attracts Aventura's Argentine community for family visits, polo season, and wine country tours. Patagonia draws adventure travelers during the Southern Hemisphere summer (December–March). Santiago serves as a business hub for South American trade. Both countries require six months of passport validity, and Argentine visa requirements can be complex for extended stays.",
      urgency: "Southern Hemisphere summer trips are planned during U.S. fall — passport checks should happen by October",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "The Bahamas & Caribbean",
      context: "Caribbean cruises from Port Everglades and the Port of Miami are a staple for Aventura families, couples, and groups of friends. The Bahamas, Jamaica, the Cayman Islands, and the Eastern Caribbean are all accessible on three- to seven-day itineraries. Aventura's proximity to both cruise ports makes spontaneous bookings common — which creates last-minute passport urgency when a family member's document is discovered expired.",
      urgency: "Cruise bookings can happen 1–3 weeks before sailing — the shortest notice window of any Aventura travel pattern",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Europe",
      context: "Paris, London, Rome, Barcelona, and Amsterdam draw Aventura travelers for business conferences, art events, fashion weeks, and extended vacations. The city's affluent residents frequently combine European city tours with Mediterranean cruises or Alpine ski trips. The Schengen Area requires three months of passport validity beyond your planned departure, and many airlines enforce six months at check-in.",
      urgency: "European summer bookings peak in March–April — passport renewals should be completed by May at the latest",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Mexico & Central America",
      context: "Cancun and Riviera Maya are weekend destinations for Aventura residents — close enough for a quick beach getaway, familiar enough for family trips, and affordable enough for spontaneous bookings. Costa Rica and Panama draw eco-tourists and real estate investors. Mexico requires six months of passport validity, and the popular all-inclusive resorts book up quickly during peak season.",
      urgency: "Spring break and summer bookings create a March–June passport renewal crunch for Aventura families",
    },
  ];

  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Where Aventura Travelers Go</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Popular International Destinations from Aventura
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Aventura travelers head to Latin America, the Caribbean, Europe, and beyond for a mix of family visits, business, cruises, and luxury vacations. These are the destinations that create the most passport urgency for our Aventura clients.
          </p>
        </div>

        {/* Stat blocks */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {stats.map((stat, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200 text-center">
              <p className="font-serif text-2xl md:text-3xl font-bold text-[#1A365D] mb-1">{stat.value}</p>
              <p className="text-gray-500 text-xs leading-relaxed">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Destination cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {destinations.map((dest, i) => (
            <div
              key={i}
              className="bg-[#F7F8FA] rounded-lg border border-gray-200 p-6 hover:border-[#F6C344] transition-all duration-200 flex flex-col"
            >
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-gray-200 flex-shrink-0">
                  <i className={`${dest.icon} text-[#1A365D] text-sm`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{dest.name}</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-1">{dest.context}</p>
              <div className="bg-white rounded-md p-3 border border-gray-200">
                <p className="text-[#F6C344] text-xs font-semibold mb-0.5"><i className="ri-time-line mr-1"></i>Urgency Pattern</p>
                <p className="text-gray-500 text-xs leading-relaxed">{dest.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Internal links */}
        <div className="text-center">
          <p className="text-gray-400 text-xs mb-3">Serving travelers throughout South Florida</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              { name: "Miami", href: "/cities/miami/" },
              { name: "Fort Lauderdale", href: "/cities/fort-lauderdale/" },
              { name: "Coral Gables", href: "/cities/coral-gables/" },
              { name: "Weston", href: "/cities/weston/" },
              { name: "Boca Raton", href: "/cities/boca-raton/" },
              { name: "West Palm Beach", href: "/cities/west-palm-beach/" },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5"
              >
                {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}